import NextImage, { getImageProps } from 'next/image'
import type { TypeCmsImage, TypeCmsMediumVariant } from '@/schema'
import { SyntheticEvent, type FC } from 'react'

// KEEP STRING ORDER FROM LARGEST TO SMALLEST
const VARIANTS_IN_PROJECT = [
  'grid_high2x', 'grid_high', 'grid_low2x',
  'pdp_high2x', 'pdp_high', 'pdp_low2x',
  'configurator_high2x', 'configurator_high', 'configurator_low2x',
  'zoom_high2x', 'zoom_high', 'zoom_low2x',
  'related_high2x', 'related_high', 'related_low2x',
  'slide_high2x', 'slide_high', 'slide_low2x',
  'cols_1_landscape_high2x', 'cols_1_landscape_high', 'cols_1_landscape_low2x',
  'cols_1_portrait_high2x', 'cols_1_portrait_high', 'cols_1_portrait_low2x',
  'cols_2_landscape_high2x', 'cols_2_landscape_high', 'cols_2_landscape_low2x',
  'cols_2_portrait_high2x', 'cols_2_portrait_high', 'cols_2_portrait_low2x',
  'cols_3_landscape_high2x', 'cols_3_landscape_high', 'cols_3_landscape_low2x',
  'cols_3_portrait_high2x', 'cols_3_portrait_high', 'cols_3_portrait_low2x',
  'cols_4_landscape_high2x', 'cols_4_landscape_high', 'cols_4_landscape_low2x',
  'cols_4_portrait_high2x', 'cols_4_portrait_high', 'cols_4_portrait_low2x',
  'full_width_high2x', 'full_width_high', 'full_width_low2x',
  'high2x', 'high', 'low2x',
  'full_high2x', 'full_high', 'full_low2x',
  'half_high2x', 'half_high', 'half_low2x',
  'thumb_high2x', 'thumb_high', 'thumb_low2x',
  'small_high2x', 'small_high', 'small_low2x',
  'menu_high2x', 'menu_high', 'menu_low2x',
  'xl', 'l', 'm', 's',
  'sliderXl', 'sliderL', 'sliderM', 'sliderS'
]

type TypeVariantsKeys = typeof VARIANTS_IN_PROJECT[number]

type TypeSize = { width: number, height: number }
type TypeVectorImage = {
  type?: 'svg'
  src: string,
	url?: string, // PARCHE porque no nos ponemos de acuerdo con backend frontend
  alt: string,
  size?: TypeSize,
  content_type?: 'image/svg+xml'
}

type Props = {
  readonly media?: TypeCmsImage | TypeVectorImage,
  readonly lazy?: boolean,
  readonly initialInView?: boolean,
  onClick?: () => void,
  onLoad?: (e:SyntheticEvent<HTMLImageElement>) => void
  readonly fill?: boolean,
  readonly outRef?: (node?: Element) => void,
  readonly className?: string,
  readonly sizes?: string,
  readonly quality?: number
  readonly ariaHidden?: boolean
  readonly unoptimized?: boolean
}

export const getMediaVariant = (
  obj: Record<string, TypeCmsMediumVariant> | undefined,
  variantNames: TypeVariantsKeys[] = VARIANTS_IN_PROJECT
): TypeCmsMediumVariant | undefined => {
  if (!obj) return undefined
  for (const key of variantNames) {
    if (key in obj) return obj[key]
  }
  return undefined
}

const Picture = ({ media, lazy, quality, ariaHidden }) => {
  const common = { alt: media.alt, sizes: '100vw' }
  const mobileVariant = getMediaVariant(media, [...VARIANTS_IN_PROJECT].reverse())
  const desktopVariant = getMediaVariant(media, VARIANTS_IN_PROJECT)

  const { props:mobileProps } = getImageProps({
    ...common,
    ...mobileVariant.size,
    quality,
    src: mobileVariant.src,
  })

  const { props:desktopProps } = getImageProps({
    ...common,
    ...desktopVariant.size,
    quality,
    src: desktopVariant.src,
  })

  return (
    <picture>
      <source media='(min-width: 768px)' srcSet={desktopProps.src} height={desktopProps.height} width={desktopProps.width} />
      <source media='(max-width: 767px)' srcSet={mobileProps.src} height={mobileProps.height} width={mobileProps.width} />
      <img loading={lazy ? 'lazy' : 'eager'} draggable='false' {...ariaHidden && { 'aria-hidden': ariaHidden }} alt='img' />
    </picture>
  )
}


function isTypeVectorImage(media: Props['media']): media is TypeVectorImage {
  return !!media && 'type' in media && media.type === 'svg'
}

const fallBackSize = { width: 200, height: 200 }

const Image = ({ media, lazy, fill, quality, ariaHidden, sizes, unoptimized, className, onLoad }) => {
  const isVector = isTypeVectorImage(media)
  const variant = isVector ? media : getMediaVariant(media, VARIANTS_IN_PROJECT)

  if (!variant?.src) return <div {...className && { className }}></div>
  if (!variant?.size?.width || !variant?.size?.height) {
    console.warn('Image component: wrong variant size params for variant.src:', variant?.src)
  }

  const sizeData = (!variant?.size?.width || !variant?.size?.height) ? fallBackSize : variant.size

  const onLoadImg = e => {
    if (e?.target) e.target.classList.add('ld')
    onLoad?.()
  }

  return (
    <NextImage src={variant.src} priority={!lazy} loading={lazy ? 'lazy' : 'eager'} {...quality !== 75 && { quality }}
      alt={media.alt ?? 'img'}
      {...(!fill && {
        height: sizeData.height,
        width: sizeData.width
      })}
      {...sizes && { sizes }}
      {...className && { className }}
      {...ariaHidden && { 'aria-hidden': ariaHidden }}
      {...(unoptimized || isVector) && { unoptimized: true }}
      onLoad={onLoadImg}
    />
  )
}

const ComponentByType = {
  'single': Image,
  'multi': Picture
}

export const Img: FC<Props> = ({ media, lazy, quality = 75, fill = false, sizes, ariaHidden = false, unoptimized = false, className = '', onLoad }) => {
  const Component = ComponentByType['single']

  return <Component media={media} lazy={lazy} quality={quality} fill={fill} sizes={sizes} ariaHidden={ariaHidden} unoptimized={unoptimized} className={className} onLoad={onLoad} />
}
